// src/services/firestore.js
import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    setDoc, 
    updateDoc, 
    query, 
    where, 
    orderBy, 
    limit,
    startAfter,
    addDoc,
    deleteDoc,
    serverTimestamp,
    GeoPoint
  } from "firebase/firestore";
  import { db } from "./firebase";
  import { createJobApplicationNotification } from './enhancedFirestore';
  
  // User Profiles
  
  export const getUserProfile = async (userType, uid) => {
    try {
      const docRef = doc(db, userType, uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error("Profile not found");
      }
    } catch (error) {
      throw error;
    }
  };
  
  export const updateUserProfile = async (userType, uid, profileData) => {
    try {
      // Get references to both documents
      const userDocRef = doc(db, "users", uid);
      const typeDocRef = doc(db, userType, uid);

      // Update both documents
      await updateDoc(userDocRef, { 
        displayName: profileData.displayName,
        updatedAt: serverTimestamp()
      });

      await updateDoc(typeDocRef, { 
        displayName: profileData.displayName, // Update root displayName
        profile: {
          ...profileData,
          displayName: profileData.displayName // Update nested displayName
        },
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      throw error;
    }
  };
  
  // Provider Specific Functions
  
  export const updateProviderLocation = async (uid, latitude, longitude, address) => {
    try {
      const docRef = doc(db, "providers", uid);
      await updateDoc(docRef, { 
        "profile.location": { latitude, longitude },
        "profile.address": address,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  export const updateProviderPrograms = async (uid, programs) => {
    try {
      const docRef = doc(db, "providers", uid);
      await updateDoc(docRef, { 
        "profile.programs": programs,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  export const updateProviderFoodMenu = async (uid, foodMenu) => {
    try {
      const docRef = doc(db, "providers", uid);
      await updateDoc(docRef, { 
        "profile.foodMenu": foodMenu,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  // Spot Listings
  
  export const addSpotListing = async (providerUid, spotData) => {
    try {
      const spotsRef = collection(db, "spots");
      const newSpot = {
        ...spotData,
        providerUid,
        createdAt: serverTimestamp(),
        status: "available"
      };
      
      const docRef = await addDoc(spotsRef, newSpot);
      return { id: docRef.id, ...newSpot };
    } catch (error) {
      throw error;
    }
  };
  
  export const updateSpotListing = async (spotId, spotData) => {
    try {
      const docRef = doc(db, "spots", spotId);
      await updateDoc(docRef, { 
        ...spotData,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  export const deleteSpotListing = async (spotId) => {
    try {
      const docRef = doc(db, "spots", spotId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  export const getProviderSpots = async (providerUid) => {
    try {
      const spotsRef = collection(db, "spots");
      const q = query(
        spotsRef, 
        where("providerUid", "==", providerUid),
        orderBy("createdAt", "desc")
      );
      
      const querySnapshot = await getDocs(q);
      const spots = [];
      
      querySnapshot.forEach((doc) => {
        spots.push({ id: doc.id, ...doc.data() });
      });
      
      return spots;
    } catch (error) {
      throw error;
    }
  };
  
  // Job Listings
  
  export const addJobListing = async (jobData) => {
    try {
      const jobsRef = collection(db, "jobs");
      const newJob = {
        ...jobData,
        createdAt: serverTimestamp(),
        status: "open"
      };
      const docRef = await addDoc(jobsRef, newJob);

      // Add jobId field to the document
      await updateDoc(docRef, { jobId: docRef.id });

      return { id: docRef.id, ...newJob, jobId: docRef.id };
    } catch (error) {
      throw error;
    }
  };
  
  export const updateJobListing = async (jobId, jobData) => {
    try {
      const docRef = doc(db, "jobs", jobId);
      await updateDoc(docRef, { 
        ...jobData,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  export const deleteJobListing = async (jobId) => {
    try {
      // Soft delete: set status to 'deleted'
      const docRef = doc(db, "jobs", jobId);
      await updateDoc(docRef, { status: 'deleted' });
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  export const getProviderJobs = async (providerUid) => {
    try {
      const jobsRef = collection(db, "jobs");
      const q = query(
        jobsRef, 
        where("providerUid", "==", providerUid),
        orderBy("createdAt", "desc")
      );
      
      const querySnapshot = await getDocs(q);
      const jobs = [];
      
      querySnapshot.forEach((doc) => {
        jobs.push({ id: doc.id, ...doc.data() });
      });
      
      return jobs;
    } catch (error) {
      throw error;
    }
  };
  
  // Waiting List
  
  export const addToWaitingList = async (providerUid, parentData) => {
    try {
      const waitingListRef = collection(db, "waitingList");
      let newPosition = 1;
      try {
        const q = query(
          waitingListRef,
          where("providerUid", "==", providerUid),
          orderBy("position", "desc"),
          limit(1)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const lastEntry = snapshot.docs[0].data();
          newPosition = (lastEntry.position || 0) + 1;
        }
      } catch (err) {
        // If the query fails (e.g., no position field exists yet), default to 1
        console.warn('No position field found in waitlist, starting at 1:', err);
        newPosition = 1;
      }
      const newEntry = {
        providerUid,
        parentUid: parentData.parentUid,
        childData: parentData.childData,
        requestData: parentData.requestData,
        createdAt: serverTimestamp(),
        status: "pending",
        position: newPosition
      };
      const docRef = await addDoc(waitingListRef, newEntry);
      return { id: docRef.id, ...newEntry };
    } catch (error) {
      console.error('Error in addToWaitingList:', error);
      throw error;
    }
  };
  
  export const getProviderWaitingList = async (providerUid) => {
    try {
      const waitingListRef = collection(db, "waitingList");
      const q = query(
        waitingListRef, 
        where("providerUid", "==", providerUid),
        orderBy("createdAt", "asc")
      );
      
      const querySnapshot = await getDocs(q);
      const waitingList = [];
      
      querySnapshot.forEach((doc) => {
        waitingList.push({ id: doc.id, ...doc.data() });
      });
      
      return waitingList;
    } catch (error) {
      throw error;
    }
  };
  
  // Search Functions
  
  export const searchProviders = async (filters, lastDoc = null, resultsPerPage = 10) => {
    try {
      let providersRef = collection(db, "providers");
      let constraints = [];
      
      // Add filters
      if (filters.type) {
        constraints.push(where("profile.type", "==", filters.type));
      }
      
      // Fix: Only add licensed filter if value is 'true' or 'false', and convert to boolean
      if (filters.licensed === 'true') {
        constraints.push(where("profile.licensed", "==", true));
      } else if (filters.licensed === 'false') {
        constraints.push(where("profile.licensed", "==", false));
      }
      
      if (filters.programs && filters.programs.length > 0) {
        // Note: This is simplified and would need a more complex query or client-side filtering
        // for array contains any with multiple values
        constraints.push(where("profile.programs", "array-contains-any", filters.programs));
      }
      
      // Base query
      let q = query(providersRef, ...constraints, orderBy("createdAt", "desc"), limit(resultsPerPage));
      
      // Pagination
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      const querySnapshot = await getDocs(q);
      const providers = [];
      let lastVisible = null;
      
      querySnapshot.forEach((doc) => {
        providers.push({ id: doc.id, ...doc.data() });
        lastVisible = doc;
      });
      
      return { providers, lastVisible };
    } catch (error) {
      throw error;
    }
  };
  
  export const searchJobSeekers = async (filters, lastDoc = null, resultsPerPage = 10) => {
    try {
      let jobSeekersRef = collection(db, "jobseekers");
      let constraints = [];
      
      if (filters.type) {
        constraints.push(where("profile.type", "in", [filters.type, "both"]));
      } else {
        // If no type is selected (All Types), do not add any type constraint
      }
      
      if (filters.criminalRecordCheck === "true") {
        constraints.push(where("profile.criminalRecordCheck", "==", true));
      } else if (filters.criminalRecordCheck === "false") {
        constraints.push(where("profile.criminalRecordCheck", "==", false));
      }
      
      if (filters.certifications && filters.certifications.length > 0) {
        constraints.push(where("profile.certifications", "array-contains-any", filters.certifications));
      }
      
      if (filters.languages && filters.languages.length > 0) {
        constraints.push(where("profile.languages", "array-contains-any", filters.languages));
      }
      
      let q = query(jobSeekersRef, ...constraints, orderBy("createdAt", "desc"), limit(resultsPerPage));
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      const querySnapshot = await getDocs(q);
      const jobSeekers = [];
      let lastVisible = null;
      
      querySnapshot.forEach((doc) => {
        jobSeekers.push({ id: doc.id, ...doc.data() });
        lastVisible = doc;
      });
      
      return { jobSeekers, lastVisible };
    } catch (error) {
      throw error;
    }
  };
  
  export const searchSpots = async (filters, lastDoc = null, resultsPerPage = 10) => {
    try {
      let spotsRef = collection(db, "spots");
      let constraints = [where("status", "==", "available")];
      
      // Add filters
      if (filters.ageCategory) {
        constraints.push(where("ageCategory", "==", filters.ageCategory));
      }
      
      if (filters.type) {
        constraints.push(where("type", "==", filters.type));
      }
      
      // Base query
      let q = query(spotsRef, ...constraints, orderBy("createdAt", "desc"), limit(resultsPerPage));
      
      // Pagination
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      const querySnapshot = await getDocs(q);
      const spots = [];
      let lastVisible = null;
      
      querySnapshot.forEach((doc) => {
        spots.push({ id: doc.id, ...doc.data() });
        lastVisible = doc;
      });
      
      return { spots, lastVisible };
    } catch (error) {
      throw error;
    }
  };
  
// Get a job by ID
export const getJobById = async (jobId) => {
  try {
    const jobDoc = await getDoc(doc(db, "jobs", jobId));
    
    if (jobDoc.exists()) {
      return {
        id: jobDoc.id,
        ...jobDoc.data()
      };
    } else {
      throw new Error("Job not found");
    }
  } catch (error) {
    console.error("Error getting job:", error);
    throw error;
  }
};

  export const searchJobs = async (filters, lastDoc = null, resultsPerPage = 10) => {
    try {
      let jobsRef = collection(db, "jobs");
      let constraints = [where("status", "==", "open")];
      
      // Add filters
      if (filters.duration) {
        constraints.push(where("duration", "==", filters.duration));
      }
      
      // Base query
      let q = query(jobsRef, ...constraints, orderBy("createdAt", "desc"), limit(resultsPerPage));
      
      // Pagination
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      const querySnapshot = await getDocs(q);
      const jobs = [];
      let lastVisible = null;
      
      querySnapshot.forEach((doc) => {
        jobs.push({ id: doc.id, ...doc.data() });
        lastVisible = doc;
      });
      
      return { jobs, lastVisible };
    } catch (error) {
      throw error;
    }
  };

export const getParentWaitingList = async (parentUid) => {
  try {
    const waitingListRef = collection(db, "waitingList");
    const q = query(
      waitingListRef, 
      where("parentUid", "==", parentUid),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    const waitingList = [];
    const providerIds = new Set();
    
    querySnapshot.forEach((doc) => {
      const waitlistEntry = { id: doc.id, waitlistId: doc.id, ...doc.data(), providerUid: doc.data().providerUid, parentUid: doc.data().parentUid };
      waitingList.push(waitlistEntry);
      providerIds.add(waitlistEntry.providerUid);
    });
    
    // Fetch provider information for all waitlists
    if (providerIds.size > 0) {
      const providersRef = collection(db, "providers");
      const providersQuery = query(
        providersRef,
        where("uid", "in", Array.from(providerIds))
      );
      
      const providersSnapshot = await getDocs(providersQuery);
      const providersData = {};
      
      providersSnapshot.forEach((doc) => {
        const providerData = doc.data();
        providersData[providerData.uid] = {
          id: doc.id,
          name: providerData.profile?.displayName || providerData.displayName || 'Unknown Provider',
          address: providerData.profile?.address || 'Address not provided',
          photo: providerData.profile?.photo || null,
          ...providerData
        };
      });
      
      // Attach provider information to waitlist entries
      for (let i = 0; i < waitingList.length; i++) {
        waitingList[i].provider = providersData[waitingList[i].providerUid] || null;
      }
    }
    
    return waitingList;
  } catch (error) {
    console.error('Error in getParentWaitingList:', error);
    throw error;
  }
};

// Job Applications
export const addJobApplication = async (applicationData) => {
  try {
    // Check for existing application
    const applicationsRef = collection(db, "applications");
    const q = query(
      applicationsRef,
      where("jobId", "==", applicationData.jobId),
      where("jobSeekerUid", "==", applicationData.jobSeekerUid)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      // Application already exists, do not add duplicate
      throw new Error("You have already applied for this job.");
    }
    // Otherwise, add new application
    const newApplication = {
      ...applicationData,
      createdAt: serverTimestamp(),
      status: 'pending'
    };
    const docRef = await addDoc(applicationsRef, newApplication);
    const application = { id: docRef.id, ...newApplication };

    // Fetch the job to get the correct owner
    const job = await getJobById(application.jobId);

    // Create notification for the job owner (parent)
    await createJobApplicationNotification({
      ...application,
      parentUid: job.parentUid // ensure this is set
    });

    return application;
  } catch (error) {
    throw error;
  }
};

export const getParentJobs = async (parentUid) => {
  try {
    const jobsRef = collection(db, "jobs");
    const q = query(
      jobsRef, 
      where("parentUid", "==", parentUid),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    const jobs = [];
    
    querySnapshot.forEach((doc) => {
      jobs.push({ id: doc.id, ...doc.data() });
    });
    
    return jobs;
  } catch (error) {
    throw error;
  }
};

// --- Children Collection CRUD ---

/**
 * Add a new child document to the 'children' collection.
 * @param {Object} childData - Must include parentUid, name, gender, dateOfBirth, etc.
 */
export const addChild = async (childData) => {
  try {
    const childrenRef = collection(db, 'children');
    const newChild = {
      ...childData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const docRef = await addDoc(childrenRef, newChild);
    return { id: docRef.id, ...newChild };
  } catch (error) {
    throw error;
  }
};

/**
 * Update an existing child document.
 */
export const updateChild = async (childId, childData) => {
  try {
    const docRef = doc(db, 'children', childId);
    await updateDoc(docRef, {
      ...childData,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a child document.
 */
export const deleteChild = async (childId) => {
  try {
    const docRef = doc(db, 'children', childId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all children for a given parentUid.
 */
export const getChildrenByParent = async (parentUid) => {
  try {
    const childrenRef = collection(db, 'children');
    const q = query(childrenRef, where('parentUid', '==', parentUid), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const children = [];
    querySnapshot.forEach((doc) => {
      children.push({ id: doc.id, ...doc.data() });
    });
    return children;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all children (for admin/provider search, etc.)
 */
export const getAllChildren = async () => {
  try {
    const childrenRef = collection(db, 'children');
    const q = query(childrenRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const children = [];
    querySnapshot.forEach((doc) => {
      children.push({ id: doc.id, ...doc.data() });
    });
    return children;
  } catch (error) {
    throw error;
  }
};

// Get applications for a job
export const getJobApplications = async (jobId) => {
  try {
    const applicationsRef = collection(db, "applications");
    const q = query(
      applicationsRef,
      where("jobId", "==", jobId),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    const applications = [];
    
    querySnapshot.forEach((doc) => {
      applications.push({ id: doc.id, ...doc.data() });
    });
    
    return applications;
  } catch (error) {
    throw error;
  }
};

// Check if jobseeker has already applied
export const hasJobSeekerApplied = async (jobId, jobSeekerUid) => {
  try {
    const applicationsRef = collection(db, "applications");
    const q = query(
      applicationsRef,
      where("jobId", "==", jobId),
      where("jobSeekerUid", "==", jobSeekerUid)
    );
    
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    throw error;
  }
};

// Get applications by provider/parent
export const getProviderApplications = async (providerUid) => {
  try {
    const applicationsRef = collection(db, "applications");
    const q = query(
      applicationsRef,
      where("providerUid", "==", providerUid),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    const applications = [];
    
    querySnapshot.forEach((doc) => {
      applications.push({ id: doc.id, ...doc.data() });
    });
    
    return applications;
  } catch (error) {
    throw error;
  }
};

export const deleteJobApplication = async (applicationId) => {
  try {
    const docRef = doc(db, "applications", applicationId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    throw error;
  }
};

// Get applications by parent (for jobs posted by this parent)
export const getParentApplications = async (parentUid) => {
  try {
    const applicationsRef = collection(db, "applications");
    const q = query(
      applicationsRef,
      where("providerUid", "==", parentUid),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);
    const applications = [];
    querySnapshot.forEach((doc) => {
      applications.push({ id: doc.id, ...doc.data() });
    });
    return applications;
  } catch (error) {
    throw error;
  }
};